import React, { useRef, useState } from 'react';

import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Alert, IconButton, Popover, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

import { lensChangeFailure, useLensChange } from '~/hooks/apis/reporting/useLensChange';

import { pageBlocks } from './editor';
import { pinnedHtmlView } from './htmlBoard';
import { type LensDocument, blockKind } from './types';

type LensSettingsProps = {
  lensId: string;
  version: number;
  document: LensDocument;
  menuAnchor?: HTMLElement | null;
  onMenuClose?: () => void;
};

type Operation = { op: 'set'; id: string; patch: Record<string, unknown> };

type Setting = {
  label: string;
  value: string;
  choices: Array<[string, string]>;
  operations: (value: string) => Array<Operation>;
};

const lensPatch = (patch: Record<string, unknown>): Array<Operation> => [{ op: 'set', id: 'lens', patch }];

const FIGURE_LOOKS: Array<[string, string, { frame: string; status: string }]> = [
  ['borderless', 'Borderless', { frame: 'none', status: 'none' }],
  ['box', 'Box', { frame: 'tile', status: 'none' }],
  ['box-status', 'Box with status', { frame: 'tile', status: 'border' }],
  ['status', 'Status only', { frame: 'none', status: 'border' }],
];

function figuresSetting(document: LensDocument): Array<Setting> {
  const figures = pageBlocks(document.page).filter((block) => ['kpis', 'kpi'].includes(blockKind(block) ?? ''));
  if (!figures.length) return [];
  const look = FIGURE_LOOKS.find(([, , keys]) => keys.frame === (figures[0].frame ?? 'tile') && keys.status === (figures[0].status ?? 'none'));
  return [
    {
      label: 'Figures',
      value: look?.[0] ?? '',
      choices: FIGURE_LOOKS.map(([value, label]) => [value, label]),
      operations: (value) => figures.map((block) => ({ op: 'set', id: block.id, patch: FIGURE_LOOKS.find(([key]) => key === value)![2] })),
    },
  ];
}

export function settingsOf(document: LensDocument): Array<Setting> {
  return [
    {
      label: 'Change as',
      value: document.period.delta ?? 'pct',
      choices: [
        ['pct', '%'],
        ['diff', 'Difference'],
        ['both', 'Both'],
        ['none', 'Off'],
      ],
      operations: (value) => lensPatch({ period: { delta: value } }),
    },
    {
      label: 'Compared values',
      value: document.period.baseline === false ? 'hide' : 'show',
      choices: [
        ['show', 'Show'],
        ['hide', 'Hide'],
      ],
      operations: (value) => lensPatch({ period: { baseline: value === 'show' ? null : false } }),
    },
    {
      label: 'Numbers',
      value: document.display.numbers ?? 'compact',
      choices: [
        ['compact', 'A$28.4M'],
        ['full', 'A$28,412,345'],
      ],
      operations: (value) => lensPatch({ display: { numbers: value } }),
    },
    {
      label: 'Filters',
      value: document.display.filters ?? 'auto',
      choices: [
        ['auto', 'Auto'],
        ['top', 'Top'],
        ['side', 'Side'],
      ],
      operations: (value) => lensPatch({ display: { filters: value } }),
    },
    {
      label: 'Frames',
      value: document.display.frame ?? 'auto',
      choices: [
        ['auto', 'Data boxed'],
        ['tile', 'All boxed'],
        ['hairline', 'Lines'],
        ['none', 'Open page'],
      ],
      operations: (value) => lensPatch({ display: { frame: value === 'auto' ? null : value } }),
    },
    {
      label: 'Full-page HTML',
      value: document.display.board === 'html' ? 'html' : 'blocks',
      choices: pinnedHtmlView(document)
        ? [
            ['blocks', 'Off'],
            ['html', 'On'],
          ]
        : [['blocks', 'Off']],
      operations: (value) => {
        if (value !== 'html') return lensPatch({ display: { board: null, view: null } });
        const view = pinnedHtmlView(document);
        return view ? lensPatch({ display: { board: 'html', view } }) : [];
      },
    },
    {
      label: 'Lens title',
      value: document.display.title ?? 'shown',
      choices: [
        ['shown', 'Shown'],
        ['hidden', 'Hidden for readers'],
      ],
      operations: (value) => lensPatch({ display: { title: value === 'shown' ? null : value } }),
    },
    ...figuresSetting(document),
    {
      label: 'Spacing',
      value: document.display.density ?? 'compact',
      choices: [
        ['compact', 'Compact'],
        ['comfortable', 'Comfortable'],
      ],
      operations: (value) => lensPatch({ display: { density: value === 'compact' ? null : value } }),
    },
    {
      label: 'Header',
      value: document.display.tone ?? 'plain',
      choices: [
        ['plain', 'Plain'],
        ['accent', 'Colour band'],
      ],
      operations: (value) => lensPatch({ display: { tone: value === 'plain' ? null : value } }),
    },
    {
      label: 'Colour treatment',
      value: document.display.colorTreatment ?? 'plain',
      choices: [
        ['plain', 'Classic'],
        ['header', 'Teal header'],
        ['sections', 'Section bands'],
        ['metrics', 'Metric spotlight'],
      ],
      operations: (value) => lensPatch({ display: { colorTreatment: value === 'plain' ? null : value } }),
    },
    ...(document.display.colorTreatment && document.display.colorTreatment !== 'plain' ? [{
      label: 'Colour accents',
      value: document.display.colorAccent ?? 'ocean',
      choices: [
        ['ocean', 'Ocean teal'],
        ['sand', 'Sandy beige'],
        ['mint', 'Mint detail'],
      ],
      operations: (value: string) => lensPatch({ display: { colorAccent: value === 'ocean' ? null : value } }),
    } satisfies Setting] : []),
    {
      label: 'Data date',
      value: document.display.freshness ?? 'auto',
      choices: [
        ['auto', 'Auto'],
        ['date', 'Date'],
        ['time', 'Time'],
        ['hidden', 'Hidden'],
      ],
      operations: (value) => lensPatch({ display: { freshness: value === 'auto' ? null : value } }),
    },
    {
      label: 'Long tails',
      value: document.display.other ?? 'group',
      choices: [
        ['group', 'Group as Other'],
        ['all', 'Show every member'],
      ],
      operations: (value) => lensPatch({ display: { other: value === 'group' ? null : value } }),
    },
    {
      label: 'Audience',
      value: document.audience ?? 'internal',
      choices: [
        ['internal', 'Luxury Escapes'],
        ['partner', 'Partners'],
      ],
      operations: (value) => lensPatch({ audience: value }),
    },
  ];
}

export default function LensSettings({ lensId, version, document, menuAnchor, onMenuClose }: LensSettingsProps) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [ownOpen, setOwnOpen] = useState(false);
  const fromMenu = onMenuClose !== undefined;
  const open = fromMenu ? Boolean(menuAnchor) : ownOpen;
  const close = fromMenu ? onMenuClose : () => setOwnOpen(false);
  const save = useLensChange(lensId);

  return (
    <>
      {fromMenu ? null : (
        <Tooltip title="Display settings">
          <IconButton aria-label="Display settings" onClick={() => setOwnOpen(true)} ref={anchor} size="small">
            <TuneOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Popover
        anchorEl={fromMenu ? menuAnchor : anchor.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={close}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack data-lens-settings spacing={1.5} sx={{ p: 2, width: 340 }}>
          {settingsOf(document).map((setting) => (
            <Stack key={setting.label} spacing={0.5}>
              <Typography color="text.secondary" variant="caption">
                {setting.label}
              </Typography>
              <ToggleButtonGroup
                aria-label={setting.label}
                disabled={save.isPending}
                exclusive
                fullWidth
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
                onChange={(_, value: string | null) => {
                  if (value && value !== setting.value) {
                    save.mutate({
                      baseVersion: version,
                      operations: setting.operations(value),
                    });
                  }
                }}
                size="small"
                value={setting.value}
              >
                {setting.choices.map(([value, label]) => (
                  <ToggleButton key={value} value={value}>
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          ))}
          {save.isError ? <Alert severity="error">{lensChangeFailure(save.error)}</Alert> : null}
        </Stack>
      </Popover>
    </>
  );
}
